import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { TurnId } from '../../types/conversation';

interface UserMessage {
  id: TurnId;
  content: string;
  elementRef?: React.RefObject<HTMLDivElement | null>;
}

interface MessageNavigatorProps {
  userMessages: UserMessage[];
  visibleMessageIds?: Set<TurnId>;
  hiddenMessageIds?: Set<TurnId>; // Messages filtered out by search
  onMessageClick: (messageId: TurnId, elementRef: React.RefObject<HTMLDivElement | null>) => void;
}

/**
 * MessageNavigator - A navigation aid for quickly jumping between user messages
 * Shows as vertical indicator bars on right side, expands on hover to show message previews
 * Each bar is aligned with its corresponding text preview in the expanded panel
 * Multiple bars can be 'blue' when their turns are visible in viewport
 */
export const MessageNavigator: React.FC<MessageNavigatorProps> = ({
  userMessages,
  visibleMessageIds = new Set<TurnId>(),
  hiddenMessageIds = new Set<TurnId>(),
  onMessageClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState<TurnId | null>(null);
  const expandTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
    }
    expandTimerRef.current = setTimeout(() => {
      setIsExpanded(true);
    }, 150);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
    }
    expandTimerRef.current = setTimeout(() => {
      setIsExpanded(false);
      setHoveredMessageId(null);
    }, 200);
  }, []);

  const truncateText = useCallback((text: string): string => {
    if (!text || text.length <= 13) return text || '(No message)';
    return text.substring(0, 13) + '...';
  }, []);

  const getBarColor = useCallback(
    (
      messageId: TurnId
    ): { bar: string; text: string; bg: string; barSize: string; barWidth: string } => {
      // Hidden by search (filtered out) - show as dimmed, non-clickable
      if (hiddenMessageIds.has(messageId)) {
        return {
          bar: 'bg-gray-200',
          text: 'text-gray-300',
          bg: 'bg-gray-50/50',
          barSize: 'h-0.5',
          barWidth: 'w-2',
        };
      }
      if (visibleMessageIds.has(messageId)) {
        return {
          bar: 'bg-blue-500',
          text: 'text-blue-600',
          bg: 'bg-blue-50',
          barSize: 'h-1.5',
          barWidth: 'w-5',
        };
      }
      if (hoveredMessageId === messageId) {
        return {
          bar: 'bg-gray-800',
          text: 'text-gray-800',
          bg: 'bg-gray-50',
          barSize: 'h-0.5',
          barWidth: 'w-3',
        };
      }
      return {
        bar: 'bg-gray-300',
        text: 'text-gray-400',
        bg: 'hover:bg-gray-50',
        barSize: 'h-0.5',
        barWidth: 'w-3',
      };
    },
    [visibleMessageIds, hoveredMessageId, hiddenMessageIds]
  );

  const handleBarClick = useCallback(
    (message: UserMessage) => {
      // Don't allow clicking on hidden (filtered) messages
      if (hiddenMessageIds.has(message.id)) {
        return;
      }
      if (message.elementRef) {
        // Add flash animation immediately to the user message bubble
        const userMessageElement = message.elementRef.current?.querySelector(
          '[data-testid="turn-user"] > div'
        );
        if (userMessageElement) {
          userMessageElement.classList.add('message-flash-animation');
          setTimeout(() => {
            userMessageElement.classList.remove('message-flash-animation');
          }, 600);
        }
        onMessageClick(message.id, message.elementRef);
      }
    },
    [onMessageClick, hiddenMessageIds]
  );

  const messagesWithPreviews = useMemo(() => {
    return userMessages.map(message => ({
      ...message,
      preview: truncateText(message.content),
    }));
  }, [userMessages, truncateText]);

  // Return null if less than 2 messages
  if (messagesWithPreviews.length < 2) {
    return null;
  }

  return (
    <div
      className='fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden lg:flex'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Expanded Panel */}
      <div
        className={`overflow-hidden transition-all duration-200 ease-out ${
          isExpanded ? 'w-48 mr-2 opacity-100' : 'w-0 mr-0 opacity-0'
        }`}
      >
        <div className='bg-white rounded-lg shadow-lg border border-gray-200 py-1'>
          {messagesWithPreviews.map(message => {
            const colors = getBarColor(message.id);
            const isHidden = hiddenMessageIds.has(message.id);
            return (
              <div
                key={message.id}
                className={`
                  px-3 py-2 transition-colors h-9 flex items-center
                  ${colors.bg}
                  ${isHidden ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                `}
                onClick={() => handleBarClick(message)}
                onMouseEnter={() => !isHidden && setHoveredMessageId(message.id)}
              >
                <span className={`text-sm flex-1 truncate ${colors.text}`}>{message.preview}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Indicator Bars - aligned with text rows */}
      <div className='flex flex-col py-1'>
        {messagesWithPreviews.map(message => {
          const colors = getBarColor(message.id);
          const isHighlighted = visibleMessageIds.has(message.id);
          const isHidden = hiddenMessageIds.has(message.id);
          return (
            <div key={message.id} className='h-9 flex items-center'>
              <div
                className={`
                  ${colors.bar}
                  ${isHighlighted ? 'h-1.5' : 'h-0.5'}
                  ${isHighlighted ? 'w-5' : 'w-3'}
                  rounded-full transition-all
                  ${isHidden ? 'cursor-not-allowed' : 'cursor-pointer'}
                `}
                onClick={() => handleBarClick(message)}
                onMouseEnter={() => !isHidden && setHoveredMessageId(message.id)}
                title={isHidden ? '(Filtered out) ' + message.preview : message.preview}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MessageNavigator;
